/**
 * Display rules shared by HOTELS search (HotelListRow, HotelObject) and Dynamic Packages card.
 * Single source: prefer same fields and ordering as HotelObject / HotelListRow.
 */

export function extractHotelBoardCodes(hotelBoards) {
  if (!Array.isArray(hotelBoards) || hotelBoards.length === 0) return [];
  return hotelBoards
    .map((b) => {
      const raw =
        typeof b === "string" ? b : b?.Board || b?.board || b?.name || b?.Name;
      if (!raw) return null;
      const parts = String(raw).trim().split(/\s+/);
      return parts[parts.length - 1].toUpperCase();
    })
    .filter(Boolean);
}

/** i18n board title — same switch as HotelListRow / HotelObject */
export function translateBoardCode(code, t) {
  if (code == null || code === "" || typeof t !== "function") return null;
  const parts = String(code).trim().toUpperCase().split(/\s+/);
  const c = parts[parts.length - 1] || String(code).trim().toUpperCase();
  switch (c) {
    case "RO":
      return t("boards.RO");
    case "BB":
      return t("boards.BB");
    case "HB":
      return t("boards.HB");
    case "FB":
      return t("boards.FB");
    case "AI":
      return t("boards.AI");
    default:
      return c;
  }
}

/**
 * @param {import("i18next").TFunction} t
 */
export function resolveHotelSearchBoardLabel(hotelBoards, selectedBoardFilters, t) {
  const codesAll = extractHotelBoardCodes(hotelBoards);
  const unique = [...new Set(codesAll)];
  if (!unique.length) return null;
  if (Array.isArray(selectedBoardFilters) && selectedBoardFilters.length > 0) {
    const match = unique.find((c) => selectedBoardFilters.includes(c));
    if (match) return translateBoardCode(match, t);
  }
  return translateBoardCode(unique[0], t);
}

/** Primary image: DefaultImage.FullSize only (HotelListRow + HotelObject card). */
export function getHotelSearchListPrimaryImageUrl(hotelsItem) {
  const u = hotelsItem?.DefaultImage?.FullSize;
  if (u == null) return null;
  const s = String(u).trim();
  return s || null;
}

/** Hero URLs for cards that mirror hotel search: one primary or []. */
export function buildHotelSearchCardHeroImageUrls(hotelsItem) {
  const p = getHotelSearchListPrimaryImageUrl(hotelsItem);
  return p ? [p] : [];
}

function firstDefined(...vals) {
  for (const x of vals) {
    if (x !== undefined && x !== null && x !== "") return x;
  }
  return null;
}

/**
 * Prefer Address (HotelObject subtitle / list row), then city · country.
 */
export function buildHotelSearchResultLocationFields(hotelsItem, searchPhrase) {
  const address = (() => {
    const a = hotelsItem?.Address;
    if (a == null) return null;
    const s = String(a).trim();
    return s || null;
  })();
  const city =
    firstDefined(
      hotelsItem?.City,
      hotelsItem?.city,
      searchPhrase?.city,
      hotelsItem?.CityName
    ) || "";
  const country =
    firstDefined(
      hotelsItem?.Country,
      hotelsItem?.country,
      searchPhrase?.country,
      hotelsItem?.CountryName
    ) || "";
  const lineFromCity = [city, country].filter(Boolean).join(" · ") || null;
  const locationLine = address || lineFromCity || null;
  return {
    locationLine,
    locationCity: city || null,
    locationCountry: country || null,
  };
}

/** Same field as HotelObject / HotelListRow StarRating */
export function getHotelSearchStarRating(hotelsItem) {
  const n = Number(hotelsItem?.StarRating);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Nights DD/MM/YYYY — same as HotelListRow meta */
export function computeNightsFromSearchPhrase(searchPhrase) {
  if (!searchPhrase?.checkIn || !searchPhrase?.checkOut) return null;
  try {
    const checkInParts = String(searchPhrase.checkIn).split("/");
    const checkOutParts = String(searchPhrase.checkOut).split("/");
    if (checkInParts.length < 3 || checkOutParts.length < 3) return null;
    const checkInDate = new Date(
      Number(checkInParts[2]),
      Number(checkInParts[1]) - 1,
      Number(checkInParts[0])
    );
    const checkOutDate = new Date(
      Number(checkOutParts[2]),
      Number(checkOutParts[1]) - 1,
      Number(checkOutParts[0])
    );
    const nights = Math.floor(
      (checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)
    );
    return nights >= 0 ? nights : null;
  } catch {
    return null;
  }
}
